/**
 * Which connected Google services still have no property/account selected for this site.
 *
 * Drives the "setup incomplete" banner on the Configure tab. It is derived from the saved
 * status — not from "the user just came back from OAuth" — so it stays visible until the gap
 * is actually closed, and it also catches a user who connected earlier and left the pickers
 * empty. Merchant Center is excluded: it is configured via service account, not a picker.
 */
import type { GoogleIntegrationStatus } from '@/services/googleIntegrationsService';

export type PickerService = 'gsc' | 'ga4' | 'ads';

/** Label with its indefinite article, so callers never have to guess a/an. */
export const PICKER_SERVICE_LABEL: Record<PickerService, string> = {
  gsc: 'a Search Console property',
  ga4: 'an Analytics property',
  ads: 'an Ads account',
};

export function missingPropertyServices(
  status: Pick<GoogleIntegrationStatus, 'connected' | 'services'> | null | undefined
): PickerService[] {
  if (!status?.connected) return [];
  const { gsc, ga4, ads } = status.services;
  const missing: PickerService[] = [];
  if (gsc?.connected && !gsc.propertyUrl) missing.push('gsc');
  if (ga4?.connected && !ga4.propertyId) missing.push('ga4');
  if (ads?.connected && !ads.customerId) missing.push('ads');
  return missing;
}

/** Human sentence for the banner, e.g. "a Search Console property and an Analytics property". */
export function describeMissing(missing: PickerService[]): string {
  const parts = missing.map((s) => PICKER_SERVICE_LABEL[s]);
  if (parts.length <= 1) return parts[0] || '';
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}
